import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableChartIcon from '@mui/icons-material/TableChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { lightTheme, darkTheme } from './theme';
import { ThemeToggle, PrintHeader, HistoryControls, UnitToggle, ManageDataMenu } from './components/ui';
import { configToURL, configFromURL } from './utils/urlSharing';
import { exportScenariosToMarkdown } from './utils/exportMarkdown';
import {
  ZFSParametersInput,
  EconomicsInput,
  PerformanceInputsInput,
  CriticalControls,
  DriveParametersInput,
} from './components/Inputs';
import {
  CapacityBreakdown,
  PerformanceAssessment,
  ResilverRisk,
  EconomicsSummary,
  PoolScenarios,
  ScenarioTable,
  PrintReport,
  TCOComparison,
} from './components/Results';

// Lazy load chart-heavy components for better initial load performance
const ReliabilityComparison = lazy(() => import('./components/Results/ReliabilityComparison').then(m => ({ default: m.ReliabilityComparison })));
const CostBreakdown = lazy(() => import('./components/Results/CostBreakdown').then(m => ({ default: m.CostBreakdown })));
const ResilverComparison = lazy(() => import('./components/Results/ResilverComparison').then(m => ({ default: m.ResilverComparison })));
const DriveReplacementScheduler = lazy(() => import('./components/Results/DriveReplacementScheduler').then(m => ({ default: m.DriveReplacementScheduler })));
import { Glossary } from './components/Help';
import { calculateCapacity } from './core/capacity';
import { calculateReliability } from './core/reliability';
import { calculatePerformance } from './core/performance';
import { calculateEconomics } from './core/economics';
import { useScenarioHistory } from './hooks/useScenarioHistory';
import { useCurrentConfig } from './hooks/useScenarios';
import { useDisplayUnits } from './hooks/useDisplayUnits';
import type {
  StorageTopology,
  DriveParameters,
  ZFSParameters,
  CapacityUtilization,
  EconomicsParameters,
  PerformanceInputs,
  PoolScenario,
} from './types';
import './App.css';
import './App.print.css';

/**
 * ZFS Calculator - Main Application
 */
function App() {
  // Theme mode with localStorage persistence
  const [mode, setMode] = useCurrentConfig<'light' | 'dark'>('light', 'zfs-calculator-theme');

  // Display units (TiB/TB) with localStorage persistence
  const displayUnits = useDisplayUnits();

  // Input state with localStorage persistence
  const [topology, setTopology] = useCurrentConfig<StorageTopology>({
    drivesPerVdev: 6,
    vdevCount: 1,
    raidLevel: 'raidz2',
  }, 'zfs-calculator-topology');

  const [drive, setDrive] = useCurrentConfig<DriveParameters>({
    driveSizeTB: 18,
    driveCost: 280,
    wattsIdle: 6.5,
    wattsLoad: 9.5,
    driveIOPS: 90,
    afr: 0.95,
    peakSequentialMBps: 255,
    driveType: 'enterprise',
  }, 'zfs-calculator-drive');

  const [zfs, setZfs] = useCurrentConfig<ZFSParameters>({
    ashift: 12,
    slopPercent: 3.125,
    recordsize: 128,
    compressionRatio: 1.0,
  }, 'zfs-calculator-zfs');

  const [utilization, setUtilization] = useCurrentConfig<CapacityUtilization>({
    poolFillPercent: 80,
    freeSpaceLimitPercent: 20,
    scrubIntervalDays: 30, // Default: monthly scrubs
  }, 'zfs-calculator-utilization');

  const [economics, setEconomics] = useCurrentConfig<EconomicsParameters>({
    electricityRate: 0.12,
    uptimeHoursPerDay: 24,
  }, 'zfs-calculator-economics');

  const [performance, setPerformance] = useCurrentConfig<PerformanceInputs>({
    workloadType: 'mixed',
    readPercentage: 0.50,
  }, 'zfs-calculator-performance');

  const [activeTab, setActiveTab] = useCurrentConfig<number>(0, 'zfs-calculator-active-tab');
  const [scenarioViewMode, setScenarioViewMode] = useCurrentConfig<'card' | 'table' | 'comparison'>('card', 'zfs-calculator-scenario-view');

  // Comparison scenarios state
  const [comparisonScenarios, setComparisonScenarios] = useCurrentConfig<PoolScenario[]>([], 'zfs-calculator-comparison');

  // Scenarios state with history tracking and localStorage persistence
  const {
    scenarios,
    setScenarios,
    undo,
    redo,
    canUndo,
    canRedo,
    historySize,
  } = useScenarioHistory();
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [undoToast, setUndoToast] = useState(false);
  const [redoToast, setRedoToast] = useState(false);
  const [shareToast, setShareToast] = useState(false);

  // Handle saving current configuration as a scenario
  const handleSaveScenario = () => {
    if (!scenarioName.trim()) return;

    const newScenario: PoolScenario = {
      id: Date.now().toString(),
      name: scenarioName,
      topology: { ...topology },
      drive: { ...drive },
      zfs: { ...zfs },
      utilization: { ...utilization },
      economics: { ...economics },
      performance: { ...performance },
    };

    setScenarios([...scenarios, newScenario]);
    setScenarioDialogOpen(false);
    setScenarioName('');
  };

  // Handle deleting a scenario with confirmation and undo hint
  const handleDeleteScenario = (id: string) => {
    const scenario = scenarios.find(s => s.id === id);
    if (!scenario) return;

    // Show confirmation dialog with undo hint
    if (confirm(`Delete scenario "${scenario.name}"?\n\nYou can undo this action with Ctrl+Z (or Cmd+Z on Mac).`)) {
      setScenarios(scenarios.filter(s => s.id !== id));
    }
  };

  // Handle undo with toast notification
  const handleUndo = () => {
    undo();
    setUndoToast(true);
  };

  // Handle redo with toast notification
  const handleRedo = () => {
    redo();
    setRedoToast(true);
  };

  // Handle share configuration - copy URL to clipboard
  const handleShareConfiguration = () => {
    try {
      const shareUrl = configToURL({ topology, drive, zfs, utilization, economics, performance });
      navigator.clipboard.writeText(shareUrl);
      setShareToast(true);
    } catch (e) {
      console.error('Failed to copy share link:', e);
      alert('Failed to copy share link to clipboard.');
    }
  };

  // Handle export to Markdown - copy to clipboard
  const handleExportMarkdown = () => {
    try {
      const markdown = exportScenariosToMarkdown(scenarios);
      navigator.clipboard.writeText(markdown);
      setShareToast(true); // Reuse the success toast
    } catch (e) {
      console.error('Failed to export to Markdown:', e);
      alert('Failed to copy Markdown to clipboard.');
    }
  };

  // Export scenarios to JSON file
  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(scenarios, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zfs-scenarios-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export scenarios:', e);
    }
  };

  // Export scenarios to CSV file
  const handleExportCSV = () => {
    try {
      if (scenarios.length === 0) {
        console.warn('No scenarios to export');
        return;
      }

      const headers = [
        'ID',
        'Name',
        'Drives per vdev',
        'vdev Count',
        'RAID Level',
        'Drive Size (TB)',
        'Drive Cost ($)',
        'Pool Fill %',
        'Compression Ratio',
      ];

      const rows = scenarios.map(s => [
        s.id,
        `"${s.name}"`,
        s.topology.drivesPerVdev,
        s.topology.vdevCount,
        s.topology.raidLevel,
        s.drive.driveSizeTB,
        s.drive.driveCost,
        s.utilization.poolFillPercent,
        s.zfs.compressionRatio,
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(r => r.join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zfs-scenarios-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export scenarios to CSV:', e);
    }
  };

  // Import scenarios from JSON file
  const handleImportJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedScenarios: PoolScenario[] = JSON.parse(content);

        if (!Array.isArray(importedScenarios)) {
          throw new Error('Invalid file format: expected an array of scenarios');
        }

        // Validate each scenario has required fields
        for (const scenario of importedScenarios) {
          if (!scenario.id || !scenario.name || !scenario.topology || !scenario.drive) {
            throw new Error('Invalid scenario format: missing required fields');
          }
        }

        setScenarios([...scenarios, ...importedScenarios]);
        console.log(`Imported ${importedScenarios.length} scenario(s)`);
      } catch (e) {
        console.error('Failed to import scenarios:', e);
        alert('Failed to import scenarios. Please check the file format.');
      }
    };
    reader.readAsText(file);
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  // Add scenario to comparison
  const handleAddToComparison = (scenario: PoolScenario) => {
    if (comparisonScenarios.length >= 3) {
      alert('Maximum 3 scenarios can be compared at once');
      return;
    }
    if (comparisonScenarios.some(s => s.id === scenario.id)) {
      alert('This scenario is already in the comparison');
      return;
    }
    setComparisonScenarios([...comparisonScenarios, scenario]);
    setScenarioViewMode('comparison');
  };

  // Remove scenario from comparison
  const handleRemoveFromComparison = (id: string) => {
    setComparisonScenarios(comparisonScenarios.filter(s => s.id !== id));
    if (comparisonScenarios.length - 1 === 0) {
      setScenarioViewMode('card');
    }
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) handleUndo();
      }
      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (canRedo) handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo]);

  // Load configuration from URL on mount
  useEffect(() => {
    const loadedConfig = configFromURL(window.location.href);
    if (loadedConfig) {
      setTopology(loadedConfig.topology);
      setDrive(loadedConfig.drive);
      setZfs(loadedConfig.zfs);
      setUtilization(loadedConfig.utilization);
      setEconomics(loadedConfig.economics);
      setPerformance(loadedConfig.performance);
    }
  }, []); // Empty dependency array - run once on mount

  // Current configuration as a PoolScenario for saving custom presets
  const currentConfig: PoolScenario = {
    id: 'current',
    name: 'Current Configuration',
    topology,
    drive,
    zfs,
    utilization,
    economics,
    performance,
  };

  // Main calculations (fast, no debounce needed)
  const results = useMemo(() => {
    const capacityResults = calculateCapacity(topology, drive, zfs, utilization);
    const reliabilityResults = calculateReliability(
      topology,
      drive,
      utilization,
      capacityResults.finalUsableTiB
    );
    const performanceResults = calculatePerformance(topology, drive, performance);
    const economicsResults = calculateEconomics(
      topology,
      drive,
      economics,
      capacityResults
    );

    return {
      capacity: capacityResults,
      reliability: reliabilityResults,
      performance: performanceResults,
      economics: economicsResults,
    };
  }, [topology, drive, zfs, utilization, performance, economics]);

  return (
    <ThemeProvider theme={mode === 'light' ? lightTheme : darkTheme}>
      <CssBaseline />
      <Container maxWidth="xl" sx={{ py: 1.5 }}>
        <PrintHeader
          title="ZFS Storage & Economics Calculator Report"
          date={new Date().toLocaleDateString()}
        />
        {/* Header Bar - Left aligned title, utilities on right */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }} className="no-print">
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            ZFS Storage & Economics Calculator
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <UnitToggle unit={displayUnits.unit} onChange={displayUnits.setUnit} />
            <PrintReport scenarios={scenarios} currentConfig={currentConfig} />
            <ThemeToggle mode={mode} onToggle={() => setMode(mode === 'light' ? 'dark' : 'light')} />
          </Box>
        </Box>

      <Grid container spacing={0} sx={{ height: 'calc(100vh - 80px)' }}>
        {/* Unified Sidebar - Full Height White Column */}
        <Grid item xs={12} lg={3} sx={{
          height: '100%',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          {/* Critical Controls Section */}
          <Box sx={{ p: 3 }}>
            <CriticalControls
              topology={topology}
              drive={drive}
              utilization={utilization}
              onTopologyChange={setTopology}
              onDriveChange={setDrive}
              onUtilizationChange={setUtilization}
            />
          </Box>

          <Divider />

          {/* Advanced Settings - Integrated into Sidebar */}
          <Box className="no-print" sx={{ p: 3 }}>
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  ⚙️ Advanced Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, px: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* ZFS Parameters */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      ZFS Tuning
                    </Typography>
                    <ZFSParametersInput
                      value={zfs}
                      onChange={setZfs}
                      driveSizeTB={drive.driveSizeTB}
                      workloadType={performance.workloadType}
                    />
                  </Box>

                  <Divider />

                  {/* Drive Parameters */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Drive Parameters
                    </Typography>
                    <DriveParametersInput value={drive} onChange={setDrive} />
                  </Box>

                  <Divider />

                  {/* Economics */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Economics
                    </Typography>
                    <EconomicsInput value={economics} onChange={setEconomics} />
                  </Box>

                  <Divider />

                  {/* Performance */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Performance
                    </Typography>
                    <PerformanceInputsInput value={performance} onChange={setPerformance} />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>

        {/* Results Dashboard - Main Panel - No Paper, Clean Surface */}
        <Grid item xs={12} lg={9} sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 100,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '1rem',
                    flexDirection: 'row',
                  },
                  '& .Mui-selected': {
                    fontWeight: 700,
                    color: 'primary.main',
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab icon={<StorageIcon />} label="Capacity" iconPosition="start" />
                <Tab icon={<SpeedIcon />} label="Performance" iconPosition="start" />
                <Tab icon={<SecurityIcon />} label="Reliability" iconPosition="start" />
                <Tab icon={<AttachMoneyIcon />} label="Economics" iconPosition="start" />
                <Tab icon={<BarChartIcon />} label="Charts" iconPosition="start" />
                <Tab icon={<BookmarkIcon />} label="Scenarios" iconPosition="start" />
              </Tabs>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: 'action.hover' }}>
                <ManageDataMenu
                  onExportCSV={handleExportCSV}
                  onExportJSON={handleExportJSON}
                  onImportJSON={handleImportJSON}
                  onSaveScenario={() => setScenarioDialogOpen(true)}
                  onShareConfiguration={handleShareConfiguration}
                  onExportMarkdown={handleExportMarkdown}
                  scenariosCount={scenarios.length}
                />
              </Box>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: 3 }}>

          {activeTab === 0 && <CapacityBreakdown results={results.capacity} economics={results.economics} />}
          {activeTab === 1 && <PerformanceAssessment results={results.performance} />}
          {activeTab === 2 && <ResilverRisk results={results.reliability} />}
          {activeTab === 3 && (
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <EconomicsSummary results={results.economics} />
                <CostBreakdown results={results.economics} />
                <DriveReplacementScheduler topology={topology} drive={drive} />
              </Box>
            </Suspense>
          )}
          {activeTab === 4 && (
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <ReliabilityComparison drive={drive} utilization={utilization} />
                <ResilverComparison drive={drive} utilization={utilization} />
              </Box>
            </Suspense>
          )}
          {activeTab === 5 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <HistoryControls
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  historySize={historySize}
                />
                <ToggleButtonGroup
                  value={scenarioViewMode}
                  exclusive
                  onChange={(_e, newMode) => newMode && setScenarioViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="card" aria-label="Card view">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="Table view">
                    <TableChartIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="comparison" aria-label="Comparison view" disabled={comparisonScenarios.length < 2}>
                    <CompareArrowsIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {scenarioViewMode === 'card' ? (
                <PoolScenarios
                  scenarios={scenarios}
                  onDeleteScenario={handleDeleteScenario}
                  onCompare={handleAddToComparison}
                />
              ) : scenarioViewMode === 'table' ? (
                <ScenarioTable
                  scenarios={scenarios}
                  onCompare={handleAddToComparison}
                />
              ) : (
                <TCOComparison
                  scenarios={comparisonScenarios}
                  onRemove={handleRemoveFromComparison}
                />
              )}
            </Box>
          )}

          {/* Print-only section: show all tabs when printing */}
          <Box className="print-only">
            <Typography className="print-section-header">Capacity Analysis</Typography>
            <CapacityBreakdown results={results.capacity} />

            <Typography className="print-section-header">Performance Analysis</Typography>
            <PerformanceAssessment results={results.performance} />

            <Typography className="print-section-header">Reliability & Resilver Risk</Typography>
            <ResilverRisk results={results.reliability} />

            <Typography className="print-section-header">Economics Analysis</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <EconomicsSummary results={results.economics} />
              <Suspense fallback={<Box></Box>}>
                <CostBreakdown results={results.economics} />
              </Suspense>
            </Box>

            <Typography className="print-section-header">Comparative Charts</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Suspense fallback={<Box></Box>}>
                <ReliabilityComparison drive={drive} utilization={utilization} />
                <ResilverComparison drive={drive} utilization={utilization} />
              </Suspense>
            </Box>

            <Typography className="print-section-header">Saved Scenarios</Typography>
            <Box>
              {scenarios.length > 0 ? (
                <ScenarioTable scenarios={scenarios} />
              ) : (
                <Typography>No saved scenarios</Typography>
              )}
            </Box>

            <Typography className="print-section-header">Glossary</Typography>
            <Glossary />
          </Box>
        </Box>
      </Box>
        </Grid>
      </Grid>

      {/* Save Scenario Dialog */}
      <Dialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Current Configuration as Scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Scenario Name"
            fullWidth
            variant="outlined"
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            placeholder="e.g., 6-drive RAIDZ2, 8-drive RAIDZ3"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScenarioDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveScenario} variant="contained" disabled={!scenarioName.trim()}>
            Save Scenario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo/Redo Toast Notifications */}
      <Snackbar
        open={undoToast}
        autoHideDuration={3000}
        onClose={() => setUndoToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setUndoToast(false)} sx={{ width: '100%' }}>
          Undo successful - restored previous state
        </Alert>
      </Snackbar>
      <Snackbar
        open={redoToast}
        autoHideDuration={3000}
        onClose={() => setRedoToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setRedoToast(false)} sx={{ width: '100%' }}>
          Redo successful - restored next state
        </Alert>
      </Snackbar>
      <Snackbar
        open={shareToast}
        autoHideDuration={3000}
        onClose={() => setShareToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShareToast(false)} sx={{ width: '100%' }}>
          Share link copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Subtle author footer */}
      <Box
        className="no-print"
        sx={{
          textAlign: 'center',
          py: 1,
          mt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          Created by Nathan Peet · <a
            href="https://github.com/nathanpeet/zfs-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            View on GitHub
          </a>
        </Typography>
      </Box>
    </Container>
    </ThemeProvider>
  );
}

export default App;
